<template>
  <div class="chat">
    <div class="chatBox">
      <TUIKit
        v-if="!isSellerEntry || hasCredentials"
        v-show="!isSellerEntry || loginState === 'ready'"
        ref="tuiKitRef"
        :key="tuiKitLoginKey"
        :SDKAppID="Number(SDKAppID)"
        :userID="userID?.toString()"
        :userSig="userSig?.toString()"
        @loginStateChange="handleLoginStateChange"
      />
      <div v-if="isSellerEntry && loginState !== 'ready'" class="chat-status" role="status">
        <p>{{ loginStatusText }}</p>
        <button v-if="loginState === 'error' || authorizationTimedOut" @click="retryLogin">
          {{ hasCredentials ? '重试登录' : '重新加载' }}
        </button>
      </div>
      <!-- 1735263383154405377 -->
    </div>
    <!-- <TUICallKit
      class="callkit-container"
      :allowedMinimized="true"
      :allowedFullScreen="false"
    /> -->
  </div>
</template>
<script lang="ts" setup>
import { TUIKit } from '../TUIKit';
import { ref, nextTick, onBeforeMount, onMounted, onUnmounted, computed, watch } from 'vue';
import {
  useRoute,
  useRouter,
  type LocationQuery,
  type LocationQueryValue,
} from 'vue-router';
import {
  readChatSession,
  writeChatSession,
  type ChatSessionStored,
} from '@/utils/chat_session_storage';
import { writeRuntimeQueryParams, isRuntimeChatType } from '@/utils/chat_runtime_params';
import {
  decryptEntryPayload,
  encryptEntryPayload,
  isEntryTimestampValid,
} from '@/utils/chat_entry_crypto';
import {
  startTencentSignPolling,
  stopTencentSignPolling,
} from '@/server/chat';
import {
  isPostMessageSenderOriginAllowed,
  resolveTrustedSourceUrlForPostMessage,
} from '@/utils/post_message_security';
import { takePendingChatEntryMessages } from '@/utils/chat_entry_inbox';
import {
  getMfaDeviceSignature,
  startMfaDeviceSignatureRefresh,
  stopMfaDeviceSignatureRefresh,
} from '@/utils/mfa_device_signature';

const ENTRY_TIMESTAMP_KEY = 'ts';
const ENTRY_ENCRYPTED_KEY = 'enc';
const ENTRY_SOURCE_KEY = 'source';
const ENTRY_SOURCE_ORIGIN_STORAGE_KEY = 'gx_chat_entry_source_origin';
const URL_STRIPPED_RELOAD_KEY = 'gx_chat_url_stripped_reloaded';
/** Plain object field: `{ chatAuth: { userID, userSig, ... } }`. */
const CHAT_AUTH_POST_MESSAGE_KEY = 'chatAuth';
/** Structured message: `{ key: 'chatAuth', ts, enc, source }` (same as encrypted URL query). */
const CHAT_AUTH_MESSAGE_TYPE_KEY = 'key';

function firstQueryValue(v: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined {
  if (v === null || v === undefined) {
    return undefined;
  }
  if (Array.isArray(v)) {
    return v[0];
  }
  return v;
}

function getPlainQueryParams(query: LocationQuery): Record<string, string> {
  const result: Record<string, string> = {};
  Object.keys(query).forEach((key) => {
    if ([ENTRY_TIMESTAMP_KEY, ENTRY_ENCRYPTED_KEY, ENTRY_SOURCE_KEY].includes(key)) {
      return;
    }
    const value = firstQueryValue(query[key]);
    if (value) {
      result[key] = value;
    }
  });
  return result;
}

function parseChatAuthPayload(raw: unknown): Record<string, string> | null {
  let obj: Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  } else if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    obj = raw as Record<string, unknown>;
  } else {
    return null;
  }
  const result: Record<string, string> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined && value !== null && String(value) !== '') {
      result[key] = String(value);
    }
  });
  return Object.keys(result).length > 0 ? result : null;
}

function getSourceFromReferrerOrOpener(): string {
  if (document.referrer) {
    return document.referrer;
  }
  try {
    return window.opener?.location?.href || '';
  } catch {
    return '';
  }
}

function readEntrySourceOrigin(): string {
  try {
    return sessionStorage.getItem(ENTRY_SOURCE_ORIGIN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function resolveFromParams(params: Record<string, string>, sourceUrl: string, timestamp: string): ChatSessionStored | null {
  const stored = readChatSession();
  const resolvedSDKAppID =
    params.SDKAppId ??
    params.SDKAppID ??
    params.sdkAppId ??
    params.sdkAppID ??
    stored?.SDKAppID;
  const resolvedUserID =
    params.userID ??
    params.userId ??
    params.userid ??
    params.uid ??
    params.accountId ??
    params.accountID ??
    stored?.userID;
  const resolvedUserSig =
    params.userSig ?? params.usersig ?? params.userSIG ?? stored?.userSig;
  if (!(resolvedSDKAppID && resolvedUserID && resolvedUserSig)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[gx_chat] Missing login params after decrypt', {
        hasSDKAppID: Boolean(resolvedSDKAppID),
        hasUserID: Boolean(resolvedUserID),
        hasUserSig: Boolean(resolvedUserSig),
        keys: Object.keys(params),
      });
    }
    return stored ?? null;
  }
  const next: ChatSessionStored = {
    SDKAppID: resolvedSDKAppID,
    userID: resolvedUserID,
    userSig: resolvedUserSig,
    token: params.token ?? stored?.token,
    macAddress: params.macAddress ?? params.macaddress ?? stored?.macAddress,
    sourceUrl: sourceUrl || stored?.sourceUrl,
    entryTimestamp: timestamp || stored?.entryTimestamp,
  };
  writeChatSession(next);
  writeRuntimeQueryParams({
    ...params,
    userID: resolvedUserID,
    SDKAppId: resolvedSDKAppID,
    userSig: resolvedUserSig,
    token: next.token || '',
    macAddress: next.macAddress || '',
    [ENTRY_SOURCE_KEY]: next.sourceUrl || '',
    [ENTRY_TIMESTAMP_KEY]: next.entryTimestamp || '',
  });
  return next;
}

function createEncryptedEntryQuery(query: LocationQuery): Record<string, string> | null {
  const plainParams = getPlainQueryParams(query);
  if (Object.keys(plainParams).length === 0) {
    return null;
  }
  const timestamp = Date.now();
  const sourceUrl =
    plainParams[ENTRY_SOURCE_KEY] ||
    getSourceFromReferrerOrOpener();
  const payload = {
    ...plainParams,
    [ENTRY_SOURCE_KEY]: sourceUrl,
  };
  const encrypted = encryptEntryPayload(payload, timestamp);
  return {
    [ENTRY_TIMESTAMP_KEY]: String(timestamp),
    [ENTRY_ENCRYPTED_KEY]: encrypted,
    [ENTRY_SOURCE_KEY]: sourceUrl,
  };
}

function resolveCredentials(query: LocationQuery): { creds: ChatSessionStored | null; shouldStripAllParamsFromUrl: boolean } {
  const hasQuery = Object.keys(query).length > 0;
  if (!hasQuery) {
    return { creds: readChatSession(), shouldStripAllParamsFromUrl: false };
  }
  const encryptedPayload = firstQueryValue(query[ENTRY_ENCRYPTED_KEY]);
  const timestampText = firstQueryValue(query[ENTRY_TIMESTAMP_KEY]) ?? '';
  const sourceFromQuery = firstQueryValue(query[ENTRY_SOURCE_KEY]) ?? '';
  if (encryptedPayload && timestampText) {
    if (!sourceFromQuery) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[gx_chat] Skip decrypt because source is missing');
      }
      return { creds: readChatSession(), shouldStripAllParamsFromUrl: true };
    }
    const timestamp = Number(timestampText);
    if (!isEntryTimestampValid(timestamp)) {
      return { creds: readChatSession(), shouldStripAllParamsFromUrl: true };
    }
    const decryptedParams = decryptEntryPayload(encryptedPayload, timestamp);
    if (!decryptedParams) {
      return { creds: readChatSession(), shouldStripAllParamsFromUrl: true };
    }
    const creds = resolveFromParams(
      decryptedParams,
      sourceFromQuery,
      timestampText
    );
    return { creds, shouldStripAllParamsFromUrl: true };
  }
  const encryptedEntryQuery = createEncryptedEntryQuery(query);
  if (encryptedEntryQuery) {
    return { creds: readChatSession(), shouldStripAllParamsFromUrl: true };
  }
  return { creds: readChatSession(), shouldStripAllParamsFromUrl: hasQuery };
}

const route = useRoute();
const router = useRouter();
const { creds, shouldStripAllParamsFromUrl } = resolveCredentials(route.query);
const SDKAppID = ref(creds?.SDKAppID ?? '');
const userID = ref(creds?.userID ?? '');
const userSig = ref(creds?.userSig ?? '');
// Source selects presentation only; all entries keep the same credential validation.
const initialSourceUrl = firstQueryValue(route.query[ENTRY_SOURCE_KEY]) || getSourceFromReferrerOrOpener();
const entrySourceUrl = ref(
  isPostMessageSenderOriginAllowed(initialSourceUrl)
    ? initialSourceUrl
    : creds?.sourceUrl || readEntrySourceOrigin()
);
const isSellerEntry = computed(() => {
  try {
    const source = new URL(entrySourceUrl.value);
    return source.hostname.startsWith('seller.') && isPostMessageSenderOriginAllowed(source.origin);
  } catch {
    return false;
  }
});
watch(entrySourceUrl, (sourceUrl) => {
  try {
    const origin = new URL(sourceUrl).origin;
    if (isPostMessageSenderOriginAllowed(origin)) {
      sessionStorage.setItem(ENTRY_SOURCE_ORIGIN_STORAGE_KEY, origin);
    }
  } catch {
    // Presentation metadata is optional when sessionStorage is unavailable.
  }
}, { immediate: true });
const mfaDeviceSignatureReady = ref(false);
const tuiKitRef = ref<InstanceType<typeof TUIKit>>();
const loginState = ref<'waiting' | 'loading' | 'ready' | 'error'>('waiting');
const authorizationTimedOut = ref(false);
let authorizationTimer: ReturnType<typeof setTimeout> | undefined;
const hasCredentials = computed(() => Boolean(SDKAppID.value && userID.value && userSig.value));
const loginStatusText = computed(() => {
  if (loginState.value === 'error') return '聊天登录失败，请重试；如仍失败，请从卖家端重新打开聊天。';
  if (loginState.value === 'loading') return '正在登录聊天…';
  if (authorizationTimedOut.value) return '未收到有效授权，请回到卖家端重新打开聊天。';
  return '正在等待卖家端授权…';
});

function handleLoginStateChange(state: 'waiting' | 'loading' | 'ready' | 'error'): void {
  loginState.value = state;
  if (state !== 'waiting') {
    clearTimeout(authorizationTimer);
    authorizationTimedOut.value = false;
  }
}

function retryLogin(): void {
  if (hasCredentials.value) {
    tuiKitRef.value?.login();
  } else {
    window.location.reload();
  }
}

const tuiKitLoginKey = computed((): string => {
  if (SDKAppID.value && userID.value && userSig.value) {
    return `${SDKAppID.value}-${userID.value}-${userSig.value}`;
  }
  return 'pending-login';
});

function applyResolvedChatSession(next: ChatSessionStored): void {
  entrySourceUrl.value = next.sourceUrl || '';
  SDKAppID.value = next.SDKAppID;
  userID.value = next.userID;
  userSig.value = next.userSig;
  void getMfaDeviceSignature()
    .then(markMfaDeviceSignatureReady)
    .catch(() => {});
}

function markMfaDeviceSignatureReady(): void {
  mfaDeviceSignatureReady.value = true;
}

function tryResolveFromEncryptedPostMessage(event: MessageEvent): boolean {
  const payload = event.data;
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }
  const record = payload as Record<string, unknown>;
  if (record[CHAT_AUTH_MESSAGE_TYPE_KEY] !== CHAT_AUTH_POST_MESSAGE_KEY) {
    return false;
  }
  if (!isPostMessageSenderOriginAllowed(event.origin)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[gx_chat] postMessage chatAuth: rejected sender origin', event.origin);
    }
    return true;
  }
  const encVal = record.enc;
  const tsVal = record.ts;
  const sourceVal = record.source;
  if (typeof encVal !== 'string' || encVal === '') {
    return false;
  }
  if (tsVal === undefined || tsVal === null || tsVal === '') {
    return false;
  }
  const timestampText = typeof tsVal === 'number' ? String(tsVal) : String(tsVal);
  const sourceFromPayload = typeof sourceVal === 'string' ? sourceVal : '';
  const trustedSourceUrl = resolveTrustedSourceUrlForPostMessage(event, sourceFromPayload || undefined);
  if (trustedSourceUrl === null) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[gx_chat] postMessage chatAuth: payload source origin does not match event.origin');
    }
    return true;
  }
  const timestamp = Number(timestampText);
  if (!isEntryTimestampValid(timestamp)) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[gx_chat] postMessage chatAuth: timestamp expired or invalid');
    }
    return true;
  }
  const decryptedParams = decryptEntryPayload(encVal, timestamp);
  if (!decryptedParams) {
    return true;
  }
  const next = resolveFromParams(decryptedParams, trustedSourceUrl, timestampText);
  if (next) {
    applyResolvedChatSession(next);
  }
  return true;
}

function handleWindowMessageForChatAuth(event: MessageEvent): void {
  try {
    const payload = event.data;
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
      return;
    }
    if (tryResolveFromEncryptedPostMessage(event)) {
      return;
    }
    const record = payload as Record<string, unknown>;
    const chatAuth = record[CHAT_AUTH_POST_MESSAGE_KEY];
    if (chatAuth === undefined) {
      return;
    }
    if (!isPostMessageSenderOriginAllowed(event.origin)) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[gx_chat] postMessage chatAuth: rejected sender origin', event.origin);
      }
      return;
    }
    const params = parseChatAuthPayload(chatAuth);
    if (!params) {
      return;
    }
    const timestampText = String(Date.now());
    const embeddedSource =
      params.source ?? params.sourceUrl ?? params.entrySource ?? '';
    const trustedSourceUrl = resolveTrustedSourceUrlForPostMessage(
      event,
      embeddedSource || undefined
    );
    if (trustedSourceUrl === null) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[gx_chat] postMessage chatAuth: payload source origin does not match event.origin');
      }
      return;
    }
    const next = resolveFromParams(params, trustedSourceUrl, timestampText);
    if (!next) {
      return;
    }
    applyResolvedChatSession(next);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[gx_chat] postMessage handler error', err);
    }
  }
}

function logNavigationRejection(reason: unknown): void {
  if (process.env.NODE_ENV !== 'production' && reason !== undefined) {
    console.warn('[gx_chat] router.replace rejected (often duplicate navigation)', reason);
  }
}

watch(
  () => ({
    ready: Boolean(SDKAppID.value && userID.value && userSig.value),
    mfaDeviceSignatureReady: mfaDeviceSignatureReady.value,
    isTkOss: isRuntimeChatType('tk_oss'),
  }),
  ({ ready, mfaDeviceSignatureReady: signatureReady, isTkOss }) => {
    if (ready && signatureReady && !isTkOss) {
      startTencentSignPolling(() => ({
        userID: userID.value,
        SDKAppID: SDKAppID.value,
      }));
    } else {
      stopTencentSignPolling();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  clearTimeout(authorizationTimer);
  stopTencentSignPolling();
  stopMfaDeviceSignatureRefresh();
  window.removeEventListener('message', handleWindowMessageForChatAuth);
});

onBeforeMount(() => {
  startMfaDeviceSignatureRefresh(markMfaDeviceSignatureReady);
});

onMounted(async () => {
  window.addEventListener('message', handleWindowMessageForChatAuth);
  if (isSellerEntry.value) {
    authorizationTimer = setTimeout(() => {
      authorizationTimedOut.value = loginState.value === 'waiting';
    }, 8000);
  }
  const pendingMessages = takePendingChatEntryMessages();
  pendingMessages.forEach(handleWindowMessageForChatAuth);
  // Auth changes mount TUIKit. Replay peer selection after its listeners exist.
  await nextTick();
  pendingMessages.forEach((event) => {
    if (event.data?.toUser !== undefined) {
      window.dispatchEvent(event);
    }
  });
  if (!shouldStripAllParamsFromUrl) {
    sessionStorage.removeItem(URL_STRIPPED_RELOAD_KEY);
    return;
  }
  if (sessionStorage.getItem(URL_STRIPPED_RELOAD_KEY) === '1') {
    sessionStorage.removeItem(URL_STRIPPED_RELOAD_KEY);
    return;
  }
  const encryptedEntryQuery = createEncryptedEntryQuery(route.query);
  if (encryptedEntryQuery) {
    void router
      .replace({
        path: route.path,
        query: encryptedEntryQuery,
      })
      .then(() => {
        window.location.reload();
      })
      .catch(logNavigationRejection);
    return;
  }
  void router
    .replace({
      path: route.path,
      query: {},
    })
    .then(() => {
      sessionStorage.setItem(URL_STRIPPED_RELOAD_KEY, '1');
      window.location.reload();
    })
    .catch(logNavigationRejection);
});
</script>
<style lang="scss">
.chat {
  width: 100;
  position: relative;
  min-height: 300px;
  background: white;
  .chatBox {
    position: relative;

    // min-width: 1000px;
  }
  .chat-status {
    display: flex;
    min-height: 300px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #606266;
    button {
      padding: 8px 20px;
      cursor: pointer;
    }
  }
}
</style>
